import { useState, useEffect, useRef } from 'react';
import { logger, type LogEntry, type LogLevel } from '../../utils/logger';

const LEVEL_COLORS: Record<LogLevel, string> = {
  debug: 'text-gray-400',
  info: 'text-blue-400',
  warn: 'text-yellow-400',
  error: 'text-red-400',
};

const LEVEL_BG: Record<LogLevel, string> = {
  debug: 'bg-gray-500/10',
  info: 'bg-blue-500/10',
  warn: 'bg-yellow-500/10',
  error: 'bg-red-500/10',
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function LogEntryItem({ entry, expanded, onToggle }: {
  entry: LogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasDetails = (entry.data !== undefined && entry.data !== null) || entry.error;

  return (
    <div className={`border-b border-gray-700 ${LEVEL_BG[entry.level]}`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors"
        disabled={!hasDetails}
      >
        <div className="flex items-start gap-2 text-xs font-mono">
          <span className="text-gray-500 shrink-0">
            {formatTime(entry.timestamp)}
          </span>
          <span className={`shrink-0 uppercase font-bold w-12 ${LEVEL_COLORS[entry.level]}`}>
            {entry.level}
          </span>
          <span className="text-primary shrink-0">
            [{entry.category}]
          </span>
          <span className="text-gray-200 flex-1 break-words">
            {entry.message}
          </span>
          {hasDetails && (
            <span className="text-gray-500 shrink-0">
              {expanded ? '▼' : '▶'}
            </span>
          )}
        </div>
      </button>

      {expanded && hasDetails && (
        <div className="px-3 pb-3 text-xs font-mono">
          {entry.error && (
            <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
              <div className="text-red-400 font-bold">{entry.error.name}</div>
              <div className="text-red-300 mt-1">{entry.error.message}</div>
              {entry.error.code && (
                <div className="text-red-400/70 mt-1">Code: {entry.error.code}</div>
              )}
              {entry.error.stack && (
                <pre className="text-red-400/60 mt-2 overflow-x-auto text-[10px] leading-tight whitespace-pre-wrap">
                  {entry.error.stack}
                </pre>
              )}
            </div>
          )}
          {entry.data !== undefined && entry.data !== null && (
            <div className="mt-2 p-2 bg-gray-500/10 rounded border border-gray-500/20">
              <div className="text-gray-400 font-bold mb-1">Data:</div>
              <pre className="text-gray-300 overflow-x-auto text-[10px] leading-tight whitespace-pre-wrap">
                {JSON.stringify(entry.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DiagnosticsPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<LogLevel | 'all'>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    return logger.subscribe(setLogs);
  }, []);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const filteredLogs = filter === 'all'
    ? logs
    : logs.filter((log) => log.level === filter);

  const errorCount = logs.filter((l) => l.level === 'error').length;
  const warnCount = logs.filter((l) => l.level === 'warn').length;

  const handleExport = () => {
    const blob = logger.exportAsBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chloropal-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(logger.export());
      alert('Logs copied to clipboard!');
    } catch {
      alert('Failed to copy logs');
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Floating button to open panel
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-600 rounded-full shadow-lg hover:bg-gray-700 transition-colors"
        aria-label="Open diagnostics panel"
      >
        <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <span className="text-xs text-gray-300 font-mono">Logs</span>
        {errorCount > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">
            {errorCount}
          </span>
        )}
        {warnCount > 0 && errorCount === 0 && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-yellow-500 text-black rounded-full">
            {warnCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col bg-gray-900 border-t border-gray-700 shadow-2xl" style={{ height: '50vh', maxHeight: '400px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-bold text-white">Diagnostics</h2>
          <span className="text-xs text-gray-400 font-mono">
            {filteredLogs.length} / {logs.length} entries
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter buttons */}
          <div className="flex gap-1">
            {(['all', 'error', 'warn', 'info', 'debug'] as const).map((level) => (
              <button
                key={level}
                onClick={() => setFilter(level)}
                className={`px-2 py-1 text-[10px] font-mono rounded transition-colors ${
                  filter === level
                    ? 'bg-primary text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {level.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Auto-scroll toggle */}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`px-2 py-1 text-[10px] font-mono rounded ${
              autoScroll ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'
            }`}
            title={autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
          >
            ↓
          </button>

          {/* Actions */}
          <button
            onClick={handleCopyToClipboard}
            className="px-2 py-1 text-[10px] font-mono bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            title="Copy logs to clipboard"
          >
            Copy
          </button>
          <button
            onClick={handleExport}
            className="px-2 py-1 text-[10px] font-mono bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            title="Export logs as JSON"
          >
            Export
          </button>
          <button
            onClick={() => logger.clear()}
            className="px-2 py-1 text-[10px] font-mono bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
            title="Clear all logs"
          >
            Clear
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="px-2 py-1 text-gray-400 hover:text-white"
            aria-label="Close diagnostics panel"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Log list */}
      <div ref={logContainerRef} className="flex-1 overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No logs yet
          </div>
        ) : (
          filteredLogs.map((entry) => (
            <LogEntryItem
              key={entry.id}
              entry={entry}
              expanded={expandedIds.has(entry.id)}
              onToggle={() => toggleExpanded(entry.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
